import { FC } from 'react';
import type { Sampling } from '../../../api/complementApi';
import { dateHelper } from '../../../helpers/help_functions/date_helpers';
import {
  ResultsLayout,
  ResultsList,
  ResultsListHeader,
  ResultsHeaderCell,
  ResultsListBody,
  ResultRow,
  ResultCell,
  ResultsDetail,
  DetailHeader,
  DetailMeta,
  ParameterResultsTitle,
  ParameterResultItem,
  ParameterName,
  ParameterValue,
  OutOfRangeBadge,
  CommentValue,
  NoSelection,
  EmptyMessage,
  CopyButton
} from './styles';

type Props = {
  samplings: Sampling[];
  selectedSampling: Sampling | null;
  onSelect: (s: Sampling) => void;
  onCopy: (s: Sampling) => void;
};

const isOutOfRange = (val: string | null | undefined) =>
  val === 'true' || (val as unknown) === true;

export const ResultsTable: FC<Props> = ({ samplings, selectedSampling, onSelect, onCopy }) => {
  if (samplings.length === 0) {
    return <EmptyMessage>Žádné výsledky nenalezeny</EmptyMessage>;
  }

  const renderParameterValue = (result: Sampling['parameter_results'][number]) => {
    const oor = isOutOfRange(result.is_out_of_range);

    switch (result.result_type) {
      case 'QUANTITATIVE':
        return (
          <>
            {result.value} {result.parameter_units}
            {oor && <OutOfRangeBadge>mimo normu</OutOfRangeBadge>}
          </>
        );
      case 'QUALITATIVE':
        return (
          <>
            {result.text_value}
            {oor && <OutOfRangeBadge>mimo normu</OutOfRangeBadge>}
          </>
        );
      case 'TEXT':
        return result.rich_text_value ? (
          <span dangerouslySetInnerHTML={{ __html: result.rich_text_value }} />
        ) : (
          <span>{result.text_value}</span>
        );
      case 'COMMENT':
        return <CommentValue>{result.comment_value}</CommentValue>;
      default:
        return (
          <>
            {result.value || result.text_value} {result.parameter_units}
            {oor && <OutOfRangeBadge>mimo normu</OutOfRangeBadge>}
          </>
        );
    }
  };

  return (
    <ResultsLayout>
      <ResultsList>
        <ResultsListHeader>
          <ResultsHeaderCell>Datum</ResultsHeaderCell>
          <ResultsHeaderCell>Čas</ResultsHeaderCell>
        </ResultsListHeader>
        <ResultsListBody>
          {samplings.map((s) => (
            <ResultRow
              key={s.id}
              $selected={selectedSampling?.id === s.id}
              onClick={() => onSelect(s)}>
              <ResultCell>{dateHelper.formatDateCZ(s.made_at)}</ResultCell>
              <ResultCell>{dateHelper.formatTimeCZ(s.made_at)}</ResultCell>
            </ResultRow>
          ))}
        </ResultsListBody>
      </ResultsList>

      <ResultsDetail>
        {selectedSampling ? (
          <>
            <DetailHeader>
              <h3>Detail záznamu</h3>
              <CopyButton type="button" onClick={() => onCopy(selectedSampling)}>
                Kopírovat
              </CopyButton>
            </DetailHeader>

            <DetailMeta>
              <p>
                <strong>Datum a čas:</strong> {dateHelper.formatDateTimeCZ(selectedSampling.made_at)}
              </p>
              {selectedSampling.external_id && (
                <p>
                  <strong>ID vzorku:</strong> {selectedSampling.external_id}
                </p>
              )}
            </DetailMeta>

            <ParameterResultsTitle>Výsledky:</ParameterResultsTitle>
            {selectedSampling.parameter_results?.map((result) => (
              <ParameterResultItem
                key={result.id}
                $outOfRange={isOutOfRange(result.is_out_of_range)}>
                <ParameterName>
                  {result.parameter_name}
                  {result.parameter_abbreviation && ` (${result.parameter_abbreviation})`}
                </ParameterName>
                <ParameterValue>{renderParameterValue(result)}</ParameterValue>
              </ParameterResultItem>
            ))}
          </>
        ) : (
          <NoSelection>Vyberte záznam ze seznamu pro zobrazení detailů</NoSelection>
        )}
      </ResultsDetail>
    </ResultsLayout>
  );
};
